import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const MARKETING_DATE_FORMAT = "YYYY-MM-DD";
export const MARKETING_TIME_ZONE = "Europe/Warsaw";
export const MAX_MARKETING_DATE_RANGE_DAYS = 366;

const DEFAULT_DATE_RANGE_DAYS = 30;
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MILLISECONDS_PER_DAY = 86_400_000;

export interface MarketingDashboardFilters {
  from: string;
  to: string;
  campaign?: string;
  rep?: string;
}

type MarketingFilterUpdates = Partial<Record<keyof MarketingDashboardFilters, string | null>>;

const readOptionalFilter = (searchParams: URLSearchParams, key: "campaign" | "rep"): string | undefined => {
  const value = searchParams.get(key)?.trim();
  return value || undefined;
};

export const isMarketingDate = (value: string | null): value is string => {
  if (!value || !DATE_PARAM_PATTERN.test(value)) {
    return false;
  }

  const parsedDate = dayjs.tz(value, MARKETING_TIME_ZONE);
  return parsedDate.isValid() && parsedDate.format(MARKETING_DATE_FORMAT) === value;
};

export const getDefaultMarketingFilters = (now: Dayjs = dayjs()): MarketingDashboardFilters => {
  const to = now.tz(MARKETING_TIME_ZONE);

  return {
    from: to.subtract(DEFAULT_DATE_RANGE_DAYS - 1, "day").format(MARKETING_DATE_FORMAT),
    to: to.format(MARKETING_DATE_FORMAT),
  };
};

const getInclusiveDateRangeDays = (firstDate: string, secondDate: string): number => {
  const firstTimestamp = Date.parse(`${firstDate}T00:00:00.000Z`);
  const secondTimestamp = Date.parse(`${secondDate}T00:00:00.000Z`);
  return Math.floor(Math.abs(secondTimestamp - firstTimestamp) / MILLISECONDS_PER_DAY) + 1;
};

export const isMarketingDateSpanWithinLimit = (firstDate: string, secondDate: string): boolean =>
  getInclusiveDateRangeDays(firstDate, secondDate) <= MAX_MARKETING_DATE_RANGE_DAYS;

export const readMarketingFilters = (
  searchParams: URLSearchParams,
  now: Dayjs = dayjs(),
): MarketingDashboardFilters => {
  const defaults = getDefaultMarketingFilters(now);
  const requestedFrom = searchParams.get("from");
  const requestedTo = searchParams.get("to");
  const from = isMarketingDate(requestedFrom) ? requestedFrom : defaults.from;
  const to = isMarketingDate(requestedTo) ? requestedTo : defaults.to;
  const dateRange = from <= to && isMarketingDateSpanWithinLimit(from, to) ? { from, to } : defaults;

  return {
    ...dateRange,
    campaign: readOptionalFilter(searchParams, "campaign"),
    rep: readOptionalFilter(searchParams, "rep"),
  };
};

export const canonicalizeMarketingSearchParams = (
  searchParams: URLSearchParams,
  filters: MarketingDashboardFilters,
): URLSearchParams => {
  return updateMarketingSearchParams(searchParams, {
    from: filters.from,
    to: filters.to,
    campaign: filters.campaign ?? null,
    rep: filters.rep ?? null,
  });
};

export const updateMarketingSearchParams = (
  searchParams: URLSearchParams,
  updates: MarketingFilterUpdates,
): URLSearchParams => {
  const updatedSearchParams = new URLSearchParams(searchParams);

  Object.entries(updates).forEach(([key, rawValue]) => {
    const value = rawValue?.trim();

    if (value) {
      updatedSearchParams.set(key, value);
    } else {
      updatedSearchParams.delete(key);
    }
  });

  return updatedSearchParams;
};

export const marketingFiltersToDateRange = (filters: MarketingDashboardFilters): [Dayjs, Dayjs] => [
  dayjs.tz(filters.from, MARKETING_TIME_ZONE),
  dayjs.tz(filters.to, MARKETING_TIME_ZONE),
];

export const formatMarketingDate = (date: Dayjs): string => date.format(MARKETING_DATE_FORMAT);

export const formatMarketingDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return "—";
  }

  const date = dayjs(value);
  return date.isValid() ? date.tz(MARKETING_TIME_ZONE).format("DD.MM.YYYY HH:mm") : "—";
};

export const toMarketingRequestParams = (filters: MarketingDashboardFilters) => ({
  from: filters.from,
  to: filters.to,
  ...(filters.campaign ? { campaign: filters.campaign } : {}),
  ...(filters.rep ? { rep: filters.rep } : {}),
});
